import WebSocketHandler from '@/components/handlers/ws-handler';
import AffiliatesLayout from '@/components/layouts/affiliates-layout';
import DepositLayout from '@/components/layouts/deposit-layout';
import RootLayout from '@/components/layouts/root-layout';
import SidebarLayout from '@/components/layouts/sidebar-layout';
import { FundedAccountGuard } from '@/components/route-guards/funded-account-guard';
import { PageSkeleton } from '@/components/ui/page-skeleton';
import { ROUTES } from '@/config/routes';
import { getAllMarketStoreStates } from '@/store/use-markets-store';
import { Suspense, lazy, useMemo } from 'react';
import {
  Navigate,
  Outlet,
  RouteObject,
  RouterProvider,
  createBrowserRouter,
  useSearchParams,
} from 'react-router-dom';
import DepositPage from './pages/deposit';
import GetFundedPage from './pages/deposit/get-funded';
import OnramperPage from './pages/deposit/onramper';
import TradePage from './pages/trade';

// Lazy load all page components for better code splitting
const NotFoundPage = lazy(() => import('./pages/not-found'));
const OAuthRedirectPage = lazy(() => import('./pages/oauth'));
const JoinDialog = lazy(
  () => import('@/features/referrals/components/join-dialog'),
);
const PortfolioPage = lazy(() => import('./pages/portfolio'));
const PrivacyPolicy = lazy(() => import('./pages/privacy-policy'));
const TermsOfService = lazy(() => import('../components/terms-of-service'));
const PropTradingTermsOfService = lazy(() =>
  import('../markdown/components').then((res) => ({
    default: res.PropTradingTermsOfService,
  })),
);
const MarketsPage = lazy(() => import('./pages/markets'));
const AccountsPage = lazy(() => import('./pages/accounts'));
const CustomNetworkWithdrawPage = lazy(
  () => import('./pages/custom-network-withdraw'),
);
const LiquidityProvidingPage = lazy(() => import('./pages/lp'));
const ReferralsPage = lazy(() => import('./pages/referrals'));
const AffiliatesPage = lazy(() => import('./pages/affiliates'));
const AffiliatesInvitePage = lazy(() => import('./pages/affiliates-invite'));
const PointsPage = lazy(() => import('./pages/points'));
const VestCapitalPage = lazy(() => import('./pages/vest-capital'));
const PaymentSuccessPage = lazy(() => import('./pages/payment-success'));
const PaymentErrorPage = lazy(() => import('./pages/payment-cancelled'));
const SupportPage = lazy(() => import('./pages/support'));
const MigrationPage = lazy(() => import('./pages/migration'));

export const DEFAULT_MARKET = 'NVDA-USD-PERP';

export function getDefaultMarket() {
  return localStorage.getItem('last-market') || DEFAULT_MARKET;
}

interface TradeNavigateProps {
  symbol?: string;
}

const TradeNavigate = ({ symbol }: TradeNavigateProps) => {
  const [searchParams] = useSearchParams();
  return (
    <Navigate
      replace
      to={{
        pathname: '/trade/' + (symbol ?? getDefaultMarket()),
        search: searchParams.size > 0 ? '?' + searchParams.toString() : '',
      }}
    />
  );
};

export default function AppRouter() {
  const router = useMemo(
    () =>
      createBrowserRouter([
        {
          path: '/',
          element: <RootLayout />,
          children: [
            {
              element: (
                <>
                  <WebSocketHandler />
                  <Outlet />
                </>
              ),
              children: [
                {
                  element: <SidebarLayout />,
                  children: [
                    {
                      index: true,
                      element: <TradeNavigate />,
                    },
                    {
                      path: '/trade',
                      element: <TradeNavigate />,
                    },
                    {
                      path: ROUTES.MARKETS,
                      element: (
                        <Suspense fallback={<PageSkeleton />}>
                          <MarketsPage />
                        </Suspense>
                      ),
                    },
                    {
                      path: ROUTES.OAUTH_REDIRECT,
                      element: (
                        <Suspense fallback={<PageSkeleton />}>
                          <OAuthRedirectPage />
                        </Suspense>
                      ),
                    },
                    {
                      path: `${ROUTES.JOIN}/:ref_code`,
                      element: (
                        <Suspense fallback={<PageSkeleton />}>
                          <JoinDialog open />
                        </Suspense>
                      ),
                    },
                    {
                      path: ROUTES.CUSTOM_NETWORK_WITHDRAW,
                      element: (
                        <Suspense fallback={<PageSkeleton />}>
                          <CustomNetworkWithdrawPage />
                        </Suspense>
                      ),
                    },
                    {
                      path: ROUTES.LIQUIDITY_PROVIDING,
                      element: (
                        <FundedAccountGuard>
                          <Suspense fallback={<PageSkeleton />}>
                            <LiquidityProvidingPage />
                          </Suspense>
                        </FundedAccountGuard>
                      ),
                    },
                    {
                      path: ROUTES.POINTS,
                      element: (
                        <FundedAccountGuard>
                          <Suspense fallback={<PageSkeleton />}>
                            <PointsPage />
                          </Suspense>
                        </FundedAccountGuard>
                      ),
                    },
                    {
                      path: ROUTES.REFERRALS,
                      element: (
                        <FundedAccountGuard>
                          <Suspense fallback={<PageSkeleton />}>
                            <ReferralsPage />
                          </Suspense>
                        </FundedAccountGuard>
                      ),
                    },
                  ],
                },
                {
                  path: ROUTES.SUPPORT,
                  element: (
                    <Suspense fallback={<PageSkeleton />}>
                      <SupportPage />
                    </Suspense>
                  ),
                },
                {
                  path: ROUTES.PORTFOLIO,
                  element: (
                    <Suspense fallback={<PageSkeleton />}>
                      <PortfolioPage />
                    </Suspense>
                  ),
                },
                {
                  path: ROUTES.MIGRATION,
                  element: (
                    <Suspense fallback={<PageSkeleton />}>
                      <MigrationPage />
                    </Suspense>
                  ),
                },
                {
                  path: ROUTES.VEST_CAPITAL,
                  element: (
                    <Suspense fallback={<PageSkeleton />}>
                      <VestCapitalPage />
                    </Suspense>
                  ),
                },
                {
                  path: ROUTES.ACCOUNTS,
                  element: (
                    <Suspense fallback={<PageSkeleton />}>
                      <AccountsPage />
                    </Suspense>
                  ),
                },
                {
                  path: ROUTES.TERMS,
                  element: (
                    <Suspense fallback={<PageSkeleton />}>
                      <TermsOfService
                        className="mx-auto w-full px-6 py-8 sm:py-12"
                        style={{ maxWidth: 1536 }}
                      />
                    </Suspense>
                  ),
                },
                {
                  path: ROUTES.PROP_TERMS,
                  element: (
                    <Suspense fallback={<PageSkeleton />}>
                      <PropTradingTermsOfService
                        withTitle
                        className="mx-auto w-full px-6 py-8 sm:py-12"
                        style={{ maxWidth: 1536 }}
                      />
                    </Suspense>
                  ),
                },
                {
                  path: ROUTES.PRIVACY,
                  element: (
                    <Suspense fallback={<PageSkeleton />}>
                      <PrivacyPolicy />
                    </Suspense>
                  ),
                },
                {
                  element: <AffiliatesLayout />,
                  children: [
                    {
                      path: ROUTES.AFFILIATES,
                      element: (
                        <Suspense fallback={<PageSkeleton />}>
                          <AffiliatesPage />
                        </Suspense>
                      ),
                    },
                    {
                      path: ROUTES.AFFILIATES_INVITE,
                      element: (
                        <Suspense fallback={<PageSkeleton />}>
                          <AffiliatesInvitePage />
                        </Suspense>
                      ),
                    },
                  ],
                },
                {
                  element: <DepositLayout />,
                  children: [
                    {
                      path: ROUTES.DEPOSIT,
                      element: <DepositPage />,
                    },
                    {
                      path: ROUTES.DEPOSIT_GET_FUNDED,
                      element: <GetFundedPage />,
                    },
                    {
                      path: ROUTES.DEPOSIT_ONRAMPER,
                      element: <OnramperPage />,
                    },
                  ],
                },
              ],
            },
            {
              element: <SidebarLayout />,
              children: [
                {
                  path: `${ROUTES.TRADE}/:symbol`,
                  element: (
                    <Suspense fallback={<PageSkeleton />}>
                      <TradePage />
                    </Suspense>
                  ),
                },
                // Assumes markets have loaded
                ...Object.entries(getAllMarketStoreStates())
                  .filter(
                    ([symbol, market]) =>
                      symbol !== market.marketSpec.displaySymbol,
                  )
                  .map(
                    ([oldSymbol, market]) =>
                      ({
                        path: `${ROUTES.TRADE}/${oldSymbol}`,
                        element: (
                          <TradeNavigate
                            symbol={market.marketSpec.displaySymbol}
                          />
                        ),
                      }) as RouteObject,
                  ),
              ],
            },
          ],
        },
        // Independent pages outside of RootLayout
        {
          path: ROUTES.PAYMENT_SUCCESS,
          element: (
            <Suspense fallback={null}>
              <PaymentSuccessPage />
            </Suspense>
          ),
        },
        {
          path: ROUTES.PAYMENT_CANCELLED,
          element: (
            <Suspense fallback={null}>
              <PaymentErrorPage />
            </Suspense>
          ),
        },
        {
          path: '*',
          element: (
            <Suspense fallback={<PageSkeleton />}>
              <NotFoundPage />
            </Suspense>
          ),
        },
      ]),
    [],
  );
  return <RouterProvider router={router} fallbackElement={<PageSkeleton />} />;
}
